import React, { useEffect, useMemo, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import Select from "react-select";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import "./ContrattiClienti.css";
import { CircleDashed, CheckCircle2 } from "lucide-react";

const parseAmount = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (!value) return 0;
  if (typeof value !== "string") return 0;
  const cleaned = value
    .replace(/\s/g, "")
    .replace(/€/g, "")
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const euroFormatter = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
});

const formatEuro = (value) => euroFormatter.format(Number(value || 0));

const isCloseAmount = (a, b, eps = 0.01) => Math.abs(a - b) < eps;

const emptyCliente = {
  nome: "",
  partitaIva: "",
  codiceFiscale: "",
  indirizzo: "",
  email: "",
  projectCodiceRiferimento: "",
  tariffeIntervento: [],
  tariffeNote: "",
  manutenzioni: [],
  reperibilita: {
    attivo: false,
    importo: "",
    frequenza: "",
    registroFatturazioni: [],
  },
};

const ensureId = (item) => ({
  id: item?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
  ...item,
});

const normalizeManutenzioni = (cliente) => {
  const getMonthIndexFromDateString = (value) => {
    if (!value || typeof value !== "string") return 0;
    const parts = value.split("-");
    if (parts.length < 2) return 0;
    const monthIndex = Number(parts[1]) - 1;
    if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11)
      return 0;
    return monthIndex;
  };

  const normalizeOffsetByMonth = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const out = {};
    Object.entries(value).forEach(([from, to]) => {
      if (typeof from !== "string") return;
      if (typeof to !== "string") return;
      if (!/^\d{4}-\d{2}$/.test(from)) return;
      if (!/^\d{4}-\d{2}$/.test(to)) return;
      out[from] = to;
    });
    return out;
  };

  const normalizeAttivita = (m) => {
    const fromArray = Array.isArray(m?.attivita) ? m.attivita : [];
    if (fromArray.length > 0) {
      return fromArray.map((a) => ({
        id: a?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        descrizione: a?.descrizione || "",
        frequenza: a?.frequenza || "",
        meseInizio:
          typeof a?.meseInizio === "number" &&
          a.meseInizio >= 0 &&
          a.meseInizio <= 11
            ? a.meseInizio
            : getMonthIndexFromDateString(a?.dataInizio || a?.scadenza),
        registroInterventi: Array.isArray(a?.registroInterventi)
          ? a.registroInterventi.map(ensureId)
          : [],
        pianificazioneOffsetByMonth: normalizeOffsetByMonth(
          a?.pianificazioneOffsetByMonth,
        ),
      }));
    }

    const legacyInterventi = Array.isArray(m?.registroInterventi)
      ? m.registroInterventi.map(ensureId)
      : [];

    return [
      {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        descrizione:
          m?.descrizione || m?.impiantoSpeciale || "Attività manutenzione",
        frequenza: m?.frequenza || "",
        meseInizio: getMonthIndexFromDateString(m?.scadenza),
        registroInterventi: legacyInterventi,
        pianificazioneOffsetByMonth: {},
      },
    ];
  };

  const fromArray = Array.isArray(cliente?.manutenzioni)
    ? cliente.manutenzioni
    : [];
  if (fromArray.length > 0) {
    return fromArray.map((m) => ({
      id: m?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      attivo: Boolean(m?.attivo),
      sede: m?.sede || "",
      descrizione: m?.descrizione || m?.impiantoSpeciale || "",
      importo: m?.importo ?? "",
      frequenza: m?.frequenza || "",
      scadenza: m?.scadenza || "",
      settoreId: m?.settoreId || "",
      settoreNome: m?.settoreNome || "",
      attivita: normalizeAttivita(m),
      registroFatturazioni: (() => {
        const base = Array.isArray(m?.registroFatturazioni)
          ? m.registroFatturazioni.map(ensureId)
          : [];
        const fromAtt = Array.isArray(m?.attivita)
          ? m.attivita
              .flatMap((a) =>
                Array.isArray(a?.registroFatturazioni)
                  ? a.registroFatturazioni.map(ensureId)
                  : [],
              )
              .filter(Boolean)
          : [];
        const seen = new Set();
        return [...base, ...fromAtt].filter((r) => {
          if (!r?.id) return false;
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
      })(),
    }));
  }

  const legacy = cliente?.manutenzione;
  if (!legacy) return [];

  return [
    {
      id: legacy?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      attivo: Boolean(legacy?.attivo),
      sede: legacy?.sede || "",
      descrizione: legacy?.descrizione || legacy?.impiantoSpeciale || "",
      importo: legacy?.importo ?? "",
      frequenza: legacy?.frequenza || "",
      scadenza: legacy?.scadenza || "",
      settoreId: legacy?.settoreId || "",
      settoreNome: legacy?.settoreNome || "",
      attivita: normalizeAttivita(legacy),
      registroFatturazioni: Array.isArray(legacy?.registroFatturazioni)
        ? legacy.registroFatturazioni.map(ensureId)
        : [],
    },
  ];
};

const normalizeTariffeIntervento = (cliente) => {
  const fromArray = Array.isArray(cliente?.tariffeIntervento)
    ? cliente.tariffeIntervento
    : [];
  if (fromArray.length > 0) {
    return fromArray.map((t) => ({
      id: t?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      descrizione: t?.descrizione || "",
      manodoperaOraria: t?.manodoperaOraria ?? "",
      dirittoChiamata: t?.dirittoChiamata ?? "",
    }));
  }

  const legacy = cliente?.tariffe;
  if (!legacy) return [];

  const hasLegacyValues =
    legacy?.manodoperaOraria != null ||
    legacy?.dirittoChiamata != null ||
    legacy?.note;

  if (!hasLegacyValues) return [];

  return [
    {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      descrizione: "Tariffa Standard",
      manodoperaOraria: legacy?.manodoperaOraria ?? "",
      dirittoChiamata: legacy?.dirittoChiamata ?? "",
    },
  ];
};

export default function ContrattiClienti() {
  const { documents: clienti, error } = useCollection("clienti");
  const { documents: projects } = useCollection(
    "projects",
    null,
    null,
    null,
    false,
  );
  const { documents: settoriDocs } = useCollection("settori");
  const { addDocument, updateDocument, deleteDocument } =
    useFirestore("clienti");

  const [searchTerm, setSearchTerm] = useState("");
  const [mainTab, setMainTab] = useState("clienti");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("generali");
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState(emptyCliente);
  const [plannerYear, setPlannerYear] = useState(new Date().getFullYear());
  const [plannerOnlyActive, setPlannerOnlyActive] = useState(true);
  const [selectedPlannerCell, setSelectedPlannerCell] = useState(null);
  const [plannerOpenByCliente, setPlannerOpenByCliente] = useState({});
  const [plannerOffsetBaseKey, setPlannerOffsetBaseKey] = useState("");
  const [plannerOffsetTargetKey, setPlannerOffsetTargetKey] = useState("");
  const [plannerOffsetSaving, setPlannerOffsetSaving] = useState(false);
  const [plannerOffsetFeedback, setPlannerOffsetFeedback] = useState(null);

  const clientiFiltrati = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = Array.isArray(clienti) ? clienti : [];
    if (!term) return list;

    return list.filter((c) => {
      const nome = (c?.nome || "").toLowerCase();
      const piva = (c?.partitaIva || "").toLowerCase();
      const cf = (c?.codiceFiscale || "").toLowerCase();
      const email = (c?.email || "").toLowerCase();
      return (
        nome.includes(term) ||
        piva.includes(term) ||
        cf.includes(term) ||
        email.includes(term)
      );
    });
  }, [clienti, searchTerm]);

  const openNew = () => {
    setEditingCliente(null);
    setFormData(emptyCliente);
    setActiveTab("generali");
    setIsModalOpen(true);
  };

  const openEdit = (cliente, initialTab = "generali") => {
    setEditingCliente(cliente);
    const { manutenzione, telefono, tariffe, ...safeCliente } = cliente || {};
    setFormData({
      ...emptyCliente,
      ...safeCliente,
      tariffeIntervento: normalizeTariffeIntervento(cliente),
      tariffeNote: safeCliente?.tariffeNote || tariffe?.note || "",
      manutenzioni: normalizeManutenzioni(cliente),
      reperibilita: {
        ...emptyCliente.reperibilita,
        ...(safeCliente?.reperibilita || {}),
        registroFatturazioni: (
          safeCliente?.reperibilita?.registroFatturazioni || []
        ).map(ensureId),
      },
    });
    setActiveTab(initialTab);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCliente(null);
  };

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const projectOptions = useMemo(() => {
    const rows = Array.isArray(projects) ? projects : [];
    return rows
      .filter((p) => (p?.codice ?? "").toString().trim())
      .map((p) => {
        const codice = (p.codice ?? "").toString().trim();
        const nome =
          (p.nome ?? "").toString().trim() ||
          (p.dettagli ?? "").toString().trim() ||
          "Senza nome";
        return { value: codice, label: `${codice} - ${nome}` };
      })
      .sort((a, b) => a.value.localeCompare(b.value, "it"));
  }, [projects]);

  const settoreOptions = useMemo(() => {
    const list = Array.isArray(settoriDocs) ? settoriDocs : [];
    return list
      .filter((s) => s && s.nome)
      .map((s) => ({ value: s.id, label: s.nome }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "it", { sensitivity: "base" }),
      );
  }, [settoriDocs]);

  const setNestedField = (section, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const addRow = (section, arrayKey, row) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayKey]: [...prev[section][arrayKey], ensureId(row)],
      },
    }));
  };

  const updateRow = (section, arrayKey, rowId, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayKey]: prev[section][arrayKey].map((r) =>
          r.id === rowId ? { ...r, [key]: value } : r,
        ),
      },
    }));
  };

  const deleteRow = (section, arrayKey, rowId) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayKey]: prev[section][arrayKey].filter((r) => r.id !== rowId),
      },
    }));
  };

  const addManutenzione = () => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: [
        ...(Array.isArray(prev.manutenzioni) ? prev.manutenzioni : []),
        {
          id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
          attivo: true,
          sede: "",
          descrizione: "",
          importo: "",
          frequenza: "",
          scadenza: "",
          settoreId: "",
          settoreNome: "",
          attivita: [],
          registroFatturazioni: [],
        },
      ],
    }));
  };

  const updateManutenzione = (manutenzioneId, patch) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) =>
        m.id === manutenzioneId ? { ...m, ...patch } : m,
      ),
    }));
  };

  const deleteManutenzione = (manutenzioneId) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).filter(
        (m) => m.id !== manutenzioneId,
      ),
    }));
  };

  const addManutenzioneRow = (manutenzioneId, arrayKey, row) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const current = Array.isArray(m[arrayKey]) ? m[arrayKey] : [];
        return { ...m, [arrayKey]: [...current, ensureId(row)] };
      }),
    }));
  };

  const updateManutenzioneRow = (
    manutenzioneId,
    arrayKey,
    rowId,
    key,
    value,
  ) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const current = Array.isArray(m[arrayKey]) ? m[arrayKey] : [];
        return {
          ...m,
          [arrayKey]: current.map((r) =>
            r.id === rowId ? { ...r, [key]: value } : r,
          ),
        };
      }),
    }));
  };

  const deleteManutenzioneRow = (manutenzioneId, arrayKey, rowId) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const current = Array.isArray(m[arrayKey]) ? m[arrayKey] : [];
        return {
          ...m,
          [arrayKey]: current.filter((r) => r.id !== rowId),
        };
      }),
    }));
  };

  const addAttivita = (manutenzioneId) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const current = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: [
            ...current,
            {
              id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
              descrizione: "",
              frequenza: "",
              meseInizio: "",
              registroInterventi: [],
              pianificazioneOffsetByMonth: {},
            },
          ],
        };
      }),
    }));
  };

  const updateAttivita = (manutenzioneId, attivitaId, patch) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const current = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: current.map((a) =>
            a.id === attivitaId ? { ...a, ...patch } : a,
          ),
        };
      }),
    }));
  };

  const deleteAttivita = (manutenzioneId, attivitaId) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const current = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: current.filter((a) => a.id !== attivitaId),
        };
      }),
    }));
  };

  const addAttivitaRow = (manutenzioneId, attivitaId, arrayKey, row) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const att = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: att.map((a) => {
            if (a.id !== attivitaId) return a;
            const current = Array.isArray(a[arrayKey]) ? a[arrayKey] : [];
            return { ...a, [arrayKey]: [...current, ensureId(row)] };
          }),
        };
      }),
    }));
  };

  const updateAttivitaRow = (
    manutenzioneId,
    attivitaId,
    arrayKey,
    rowId,
    key,
    value,
  ) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const att = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: att.map((a) => {
            if (a.id !== attivitaId) return a;
            const current = Array.isArray(a[arrayKey]) ? a[arrayKey] : [];
            return {
              ...a,
              [arrayKey]: current.map((r) =>
                r.id === rowId ? { ...r, [key]: value } : r,
              ),
            };
          }),
        };
      }),
    }));
  };

  const deleteAttivitaRow = (manutenzioneId, attivitaId, arrayKey, rowId) => {
    setFormData((prev) => ({
      ...prev,
      manutenzioni: (prev.manutenzioni || []).map((m) => {
        if (m.id !== manutenzioneId) return m;
        const att = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: att.map((a) => {
            if (a.id !== attivitaId) return a;
            const current = Array.isArray(a[arrayKey]) ? a[arrayKey] : [];
            return { ...a, [arrayKey]: current.filter((r) => r.id !== rowId) };
          }),
        };
      }),
    }));
  };

  const addTariffaIntervento = () => {
    setFormData((prev) => ({
      ...prev,
      tariffeIntervento: [
        ...(Array.isArray(prev.tariffeIntervento)
          ? prev.tariffeIntervento
          : []),
        {
          id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
          descrizione: "",
          manodoperaOraria: "",
          dirittoChiamata: "",
        },
      ],
    }));
  };

  const updateTariffaIntervento = (tariffaId, patch) => {
    setFormData((prev) => ({
      ...prev,
      tariffeIntervento: (prev.tariffeIntervento || []).map((t) =>
        t.id === tariffaId ? { ...t, ...patch } : t,
      ),
    }));
  };

  const deleteTariffaIntervento = (tariffaId) => {
    setFormData((prev) => ({
      ...prev,
      tariffeIntervento: (prev.tariffeIntervento || []).filter(
        (t) => t.id !== tariffaId,
      ),
    }));
  };

  const handleSave = async () => {
    const payload = { ...formData };
    delete payload.telefono;
    delete payload.manutenzione;

    if (editingCliente?.id) {
      await updateDocument(editingCliente.id, {
        ...payload,
        tariffe: firebase.firestore.FieldValue.delete(),
      });
    } else {
      await addDocument(payload);
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (!editingCliente?.id) return;
    const ok = window.confirm("Eliminare questo cliente?");
    if (!ok) return;
    await deleteDocument(editingCliente.id);
    closeModal();
  };

  const mesi = useMemo(
    () => [
      "Gen",
      "Feb",
      "Mar",
      "Apr",
      "Mag",
      "Giu",
      "Lug",
      "Ago",
      "Set",
      "Ott",
      "Nov",
      "Dic",
    ],
    [],
  );

  const parseYearMonthFromDateString = (value) => {
    if (!value || typeof value !== "string") return null;
    const parts = value.split("-");
    if (parts.length < 2) return null;
    const year = Number(parts[0]);
    const monthIndex = Number(parts[1]) - 1;
    if (!Number.isFinite(year) || year < 1900) return null;
    if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11)
      return null;
    return { year, monthIndex };
  };

  const toMonthKey = (year, monthIndex) => {
    const mm = String((monthIndex ?? 0) + 1).padStart(2, "0");
    return `${year}-${mm}`;
  };

  const monthKeyToLabel = (monthKey) => {
    if (!monthKey || typeof monthKey !== "string") return "—";
    const parts = monthKey.split("-");
    if (parts.length < 2) return monthKey;
    const year = parts[0];
    const monthIndex = Number(parts[1]) - 1;
    const label = mesi[monthIndex] || parts[1];
    return `${label} ${year}`;
  };

  const isMonthExpected = (attivita, manutenzione, year, monthIndex) => {
    if (!manutenzione || manutenzione.attivo === false) return false;
    if (!attivita || !attivita.frequenza) return false;

    const hasExplicitStart =
      typeof attivita.meseInizio === "number" &&
      attivita.meseInizio >= 0 &&
      attivita.meseInizio <= 11
        ? true
        : false;

    let startMonthIndex = hasExplicitStart ? attivita.meseInizio : 0;

    const anchor = parseYearMonthFromDateString(manutenzione.scadenza);
    if (!hasExplicitStart && !anchor) return false;
    if (!hasExplicitStart && anchor && anchor.year <= year) {
      startMonthIndex = anchor.monthIndex;
    }

    const freq = String(attivita.frequenza || "").toLowerCase();
    if (freq === "mensile") return true;
    if (freq === "trimestrale")
      return (monthIndex - startMonthIndex + 12) % 3 === 0;
    if (freq === "quadrimestrale")
      return (monthIndex - startMonthIndex + 12) % 4 === 0;
    if (freq === "semestrale")
      return (monthIndex - startMonthIndex + 12) % 6 === 0;
    if (freq === "annuale") return monthIndex === startMonthIndex;
    return false;
  };

  const plannerRows = useMemo(() => {
    const list = Array.isArray(clientiFiltrati) ? clientiFiltrati : [];
    const rows = [];
    list.forEach((c) => {
      const manutenzioni = normalizeManutenzioni(c);
      manutenzioni.forEach((m) => {
        if (plannerOnlyActive && !Boolean(m.attivo)) return;
        const attivita = Array.isArray(m.attivita) ? m.attivita : [];
        attivita.forEach((a) => {
          rows.push({
            rowId: `${c.id}__${m.id}__${a.id}`,
            clienteId: c.id,
            clienteNome: c?.nome || "—",
            manutenzioneId: m.id,
            sede: m.sede || "",
            descrizione: m.descrizione || "",
            importo: m.importo ?? "",
            scadenza: m.scadenza || "",
            attivo: Boolean(m.attivo),
            attivitaId: a.id,
            attivitaDescrizione: a.descrizione || "",
            attivitaFrequenza: a.frequenza || "",
            attivitaMeseInizio:
              typeof a.meseInizio === "number" ? a.meseInizio : null,
            attivitaOffsetByMonth:
              a?.pianificazioneOffsetByMonth &&
              typeof a.pianificazioneOffsetByMonth === "object" &&
              !Array.isArray(a.pianificazioneOffsetByMonth)
                ? a.pianificazioneOffsetByMonth
                : {},
            registroInterventi: Array.isArray(a.registroInterventi)
              ? a.registroInterventi
              : [],
            registroFatturazioni: Array.isArray(m.registroFatturazioni)
              ? m.registroFatturazioni
              : [],
            __cliente: c,
          });
        });
      });
    });
    return rows;
  }, [clientiFiltrati, plannerOnlyActive]);

  const plannerGroups = useMemo(() => {
    const map = new Map();
    (Array.isArray(plannerRows) ? plannerRows : []).forEach((row) => {
      const key = row?.clienteId || row?.clienteNome || "unknown";
      const current = map.get(key);
      if (current) {
        current.rows.push(row);
      } else {
        map.set(key, {
          key,
          clienteNome: row?.clienteNome || "—",
          rows: [row],
        });
      }
    });

    const groups = Array.from(map.values());
    groups.sort((a, b) =>
      String(a.clienteNome || "").localeCompare(
        String(b.clienteNome || ""),
        "it",
        {
          sensitivity: "base",
        },
      ),
    );
    groups.forEach((g) => {
      g.rows.sort((ra, rb) => {
        const sedeA = String(ra?.sede || "");
        const sedeB = String(rb?.sede || "");
        const sedeCmp = sedeA.localeCompare(sedeB, "it", {
          sensitivity: "base",
        });
        if (sedeCmp !== 0) return sedeCmp;
        return String(ra?.attivitaDescrizione || "").localeCompare(
          String(rb?.attivitaDescrizione || ""),
          "it",
          { sensitivity: "base" },
        );
      });
    });

    return groups;
  }, [plannerRows]);

  const togglePlannerCliente = (key) => {
    setPlannerOpenByCliente((prev) => {
      const safePrev = prev || {};
      const current = safePrev[key];
      const isOpen = typeof current === "boolean" ? current : true;
      return { ...safePrev, [key]: !isOpen };
    });
  };

  const getMonthStats = (row, year, monthIndex) => {
    const currentMonthKey = toMonthKey(year, monthIndex);
    const offsets =
      row?.attivitaOffsetByMonth &&
      typeof row.attivitaOffsetByMonth === "object" &&
      !Array.isArray(row.attivitaOffsetByMonth)
        ? row.attivitaOffsetByMonth
        : {};

    const interventi = (row.registroInterventi || []).filter((r) => {
      const ym = parseYearMonthFromDateString(r?.data);
      return ym && ym.year === year && ym.monthIndex === monthIndex;
    });

    const baseExpected = isMonthExpected(
      {
        frequenza: row.attivitaFrequenza,
        meseInizio: row.attivitaMeseInizio,
      },
      { attivo: row.attivo, scadenza: row.scadenza },
      year,
      monthIndex,
    );

    const movedAwayTo =
      baseExpected && offsets[currentMonthKey] ? offsets[currentMonthKey] : "";
    const movedHereFrom =
      Object.entries(offsets).find(([, to]) => to === currentMonthKey)?.[0] ||
      "";

    let expected = baseExpected;
    if (expected && movedAwayTo) expected = false;
    if (movedHereFrom) expected = true;

    return {
      expected,
      baseExpected,
      movedAwayTo,
      movedHereFrom,
      interventi,
      interventiCount: interventi.length,
    };
  };

  useEffect(() => {
    if (!selectedPlannerCell?.row) {
      setPlannerOffsetBaseKey("");
      setPlannerOffsetTargetKey("");
      setPlannerOffsetFeedback(null);
      return;
    }

    const { row, currentMonthKey, baseExpected, movedHereFrom } =
      selectedPlannerCell;
    const baseKey = baseExpected ? currentMonthKey : movedHereFrom;
    const offsets =
      row?.attivitaOffsetByMonth &&
      typeof row.attivitaOffsetByMonth === "object" &&
      !Array.isArray(row.attivitaOffsetByMonth)
        ? row.attivitaOffsetByMonth
        : {};
    const existingTarget = baseKey ? offsets[baseKey] || baseKey : "";

    setPlannerOffsetBaseKey(baseKey || "");
    setPlannerOffsetTargetKey(existingTarget || "");
    setPlannerOffsetFeedback(null);
  }, [selectedPlannerCell]);

  const savePlannerOffset = async () => {
    if (!selectedPlannerCell?.row) return;
    if (!plannerOffsetBaseKey) return;
    if (!plannerOffsetTargetKey) return;

    const { row } = selectedPlannerCell;
    const cliente = row.__cliente;
    if (!cliente?.id) return;

    setPlannerOffsetSaving(true);
    setPlannerOffsetFeedback(null);
    try {
      const manutenzioni = Array.isArray(cliente?.manutenzioni)
        ? cliente.manutenzioni
        : Array.isArray(cliente?.manutenzione)
          ? cliente.manutenzione
          : cliente?.manutenzione
            ? [cliente.manutenzione]
            : [];

      const updatedManutenzioni = manutenzioni.map((m) => {
        if (!m || m.id !== row.manutenzioneId) return m;
        const attivita = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: attivita.map((a) => {
            if (!a || a.id !== row.attivitaId) return a;
            const current =
              a?.pianificazioneOffsetByMonth &&
              typeof a.pianificazioneOffsetByMonth === "object" &&
              !Array.isArray(a.pianificazioneOffsetByMonth)
                ? { ...a.pianificazioneOffsetByMonth }
                : {};

            if (plannerOffsetTargetKey === plannerOffsetBaseKey) {
              delete current[plannerOffsetBaseKey];
            } else {
              current[plannerOffsetBaseKey] = plannerOffsetTargetKey;
            }

            return { ...a, pianificazioneOffsetByMonth: current };
          }),
        };
      });

      await updateDocument(cliente.id, { manutenzioni: updatedManutenzioni });
      setPlannerOffsetFeedback({
        type: "success",
        message: "Spostamento salvato correttamente.",
      });
    } catch (e) {
      setPlannerOffsetFeedback({
        type: "error",
        message: "Errore durante il salvataggio dello spostamento.",
      });
    } finally {
      setPlannerOffsetSaving(false);
    }
  };

  const deletePlannerOffset = async () => {
    if (!selectedPlannerCell?.row) return;
    if (!plannerOffsetBaseKey) return;

    const { row } = selectedPlannerCell;
    const cliente = row.__cliente;
    if (!cliente?.id) return;

    setPlannerOffsetSaving(true);
    setPlannerOffsetFeedback(null);
    try {
      const manutenzioni = Array.isArray(cliente?.manutenzioni)
        ? cliente.manutenzioni
        : cliente?.manutenzione
          ? [cliente.manutenzione]
          : [];

      const updatedManutenzioni = manutenzioni.map((m) => {
        if (!m || m.id !== row.manutenzioneId) return m;
        const attivita = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: attivita.map((a) => {
            if (!a || a.id !== row.attivitaId) return a;
            const current =
              a?.pianificazioneOffsetByMonth &&
              typeof a.pianificazioneOffsetByMonth === "object" &&
              !Array.isArray(a.pianificazioneOffsetByMonth)
                ? { ...a.pianificazioneOffsetByMonth }
                : {};
            delete current[plannerOffsetBaseKey];
            return { ...a, pianificazioneOffsetByMonth: current };
          }),
        };
      });

      await updateDocument(cliente.id, { manutenzioni: updatedManutenzioni });
      setPlannerOffsetFeedback({
        type: "success",
        message: "Pianificazione ripristinata correttamente.",
      });
    } catch (e) {
      setPlannerOffsetFeedback({
        type: "error",
        message: "Errore durante il ripristino della pianificazione.",
      });
    } finally {
      setPlannerOffsetSaving(false);
    }
  };

  return (
    <div className="contratti-clienti">
      <div className="cc-header">
        <div>
          <h1>Contratti Clienti</h1>
          <p className="cc-subtitle">
            Anagrafiche e condizioni contrattuali per cliente
          </p>
        </div>
        <div className="cc-header-actions">
          <input
            type="text"
            className="cc-search"
            placeholder="Cerca cliente, P.IVA, CF, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            type="button"
            className="cc-btn cc-btn-primary"
            onClick={openNew}
          >
            Nuovo Cliente
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="cc-page-tabs">
        <button
          type="button"
          className={`cc-page-tab ${mainTab === "clienti" ? "active" : ""}`}
          onClick={() => setMainTab("clienti")}
        >
          Clienti
        </button>
        <button
          type="button"
          className={`cc-page-tab ${
            mainTab === "pianificazione" ? "active" : ""
          }`}
          onClick={() => setMainTab("pianificazione")}
        >
          Pianificazione
        </button>
      </div>

      {mainTab === "clienti" && (
        <div className="cc-table-wrap">
          <table className="cc-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>P.IVA / CF</th>
                <th>Manutenzione</th>
                <th>Reperibilità</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {clientiFiltrati.map((c) => (
                <tr key={c.id}>
                  <td>{c?.nome || "—"}</td>
                  <td>{c?.partitaIva || c?.codiceFiscale || "—"}</td>
                  <td>
                    {(() => {
                      const items = normalizeManutenzioni(c);
                      const totalCanoni = items.filter((m) =>
                        Boolean(m.attivo),
                      ).length;
                      const totalCanone = items.reduce((sum, m) => {
                        if (!m?.attivo) return sum;
                        return sum + parseAmount(m?.importo);
                      }, 0);
                      const totalFatture = items.reduce((sum, m) => {
                        const rows = Array.isArray(m?.registroFatturazioni)
                          ? m.registroFatturazioni
                          : [];
                        return (
                          sum +
                          rows.reduce(
                            (acc, r) => acc + parseAmount(r?.importo),
                            0,
                          )
                        );
                      }, 0);

                      if (
                        totalCanoni === 0 &&
                        totalCanone === 0 &&
                        totalFatture === 0
                      ) {
                        return <span className="cc-billing-empty">—</span>;
                      }

                      const complete =
                        totalCanone > 0 &&
                        isCloseAmount(totalCanone, totalFatture);

                      return (
                        <div className="cc-billing-cell">
                          <div className="cc-billing-amounts">
                            <span>Canone: {formatEuro(totalCanone)}</span>
                            <span>Fatture: {formatEuro(totalFatture)}</span>
                          </div>
                          <span
                            className={`cc-billing-chip ${
                              complete ? "done" : "due"
                            }`}
                          >
                            {complete ? (
                              <CheckCircle2 size={16} />
                            ) : (
                              <CircleDashed size={16} />
                            )}
                            {complete ? "Completato" : "Da completare"}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const rep = c?.reperibilita || null;
                      const totalCanone = parseAmount(rep?.importo);
                      const rows = Array.isArray(rep?.registroFatturazioni)
                        ? rep.registroFatturazioni
                        : [];
                      const totalFatture = rows.reduce(
                        (sum, r) => sum + parseAmount(r?.importo),
                        0,
                      );

                      const hasRep =
                        Boolean(rep?.attivo) ||
                        totalCanone > 0 ||
                        Boolean(rep?.frequenza) ||
                        totalFatture > 0;

                      if (!hasRep)
                        return <span className="cc-billing-empty">—</span>;

                      const complete =
                        totalCanone > 0 &&
                        isCloseAmount(totalCanone, totalFatture);

                      return (
                        <div className="cc-billing-cell">
                          <div className="cc-billing-amounts">
                            <span>Canone: {formatEuro(totalCanone)}</span>
                            <span>Fatture: {formatEuro(totalFatture)}</span>
                          </div>
                          <span
                            className={`cc-billing-chip ${
                              complete ? "done" : "due"
                            }`}
                          >
                            {complete ? (
                              <CheckCircle2 size={16} />
                            ) : (
                              <CircleDashed size={16} />
                            )}
                            {complete ? "Completato" : "Da completare"}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="cc-btn cc-btn-secondary"
                      onClick={() => openEdit(c)}
                    >
                      Dettagli
                    </button>
                  </td>
                </tr>
              ))}

              {(!clientiFiltrati || clientiFiltrati.length === 0) && (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    Nessun cliente.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {mainTab === "pianificazione" && (
        <div className="cc-planner">
          <div className="cc-planner-toolbar">
            <div className="cc-planner-controls">
              <div className="cc-field cc-planner-year">
                <label>Anno</label>
                <input
                  type="number"
                  value={plannerYear}
                  onChange={(e) => setPlannerYear(Number(e.target.value))}
                />
              </div>
              <label className="cc-checkbox cc-planner-only-active">
                <input
                  type="checkbox"
                  checked={plannerOnlyActive}
                  onChange={(e) => setPlannerOnlyActive(e.target.checked)}
                />
                Solo attive
              </label>
            </div>
            <div className="cc-planner-hint">
              Rosso = attività prevista senza intervento. Verde = attività
              completata.
            </div>
          </div>

          {plannerRows.length === 0 ? (
            <div className="cc-empty-block" style={{ textAlign: "center" }}>
              Nessuna manutenzione da mostrare.
            </div>
          ) : (
            <div className="cc-planner-client-list">
              {plannerGroups.map((group) => {
                const current = plannerOpenByCliente[group.key];
                const isOpen = typeof current === "boolean" ? current : true;

                return (
                  <div key={group.key} className="cc-planner-client-section">
                    <div
                      className="cc-planner-client-header"
                      role="button"
                      tabIndex={0}
                      onClick={() => togglePlannerCliente(group.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          togglePlannerCliente(group.key);
                        }
                      }}
                    >
                      <span className="cc-planner-client-header-title">
                        {group.clienteNome}
                      </span>
                      <span className="cc-planner-client-header-chevron">
                        {isOpen ? "▾" : "▸"}
                      </span>
                    </div>
                    {isOpen && (
                      <div className="cc-planner-table-wrap">
                        <table className="cc-planner-table">
                          <thead>
                            <tr>
                              <th className="cc-planner-col-main">
                                Sede / Attività
                              </th>
                              {mesi.map((m) => (
                                <th key={m} className="cc-planner-col-month">
                                  {m}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {group.rows.map((row) => (
                              <tr key={row.rowId}>
                                <td className="cc-planner-col-main">
                                  <div className="cc-planner-row-title">
                                    <div className="cc-planner-client">
                                      {row.sede || "—"}
                                    </div>
                                    <div className="cc-planner-meta">
                                      {row.attivitaDescrizione || "—"}
                                    </div>
                                  </div>
                                </td>
                                {mesi.map((_, monthIndex) => {
                                  const stats = getMonthStats(
                                    row,
                                    plannerYear,
                                    monthIndex,
                                  );
                                  const hasAny = stats.interventiCount > 0;
                                  let cellVariant = "empty";
                                  if (stats.expected && !hasAny)
                                    cellVariant = "due";
                                  if (stats.expected && hasAny)
                                    cellVariant = "done";

                                  const isSelected =
                                    selectedPlannerCell?.rowId === row.rowId &&
                                    selectedPlannerCell?.monthIndex ===
                                      monthIndex &&
                                    selectedPlannerCell?.year === plannerYear;

                                  return (
                                    <td key={`${row.rowId}__${monthIndex}`}>
                                      <button
                                        type="button"
                                        className={`cc-planner-cell cc-planner-${cellVariant} ${
                                          isSelected ? "selected" : ""
                                        }`}
                                        onClick={() =>
                                          setSelectedPlannerCell({
                                            rowId: row.rowId,
                                            monthIndex,
                                            year: plannerYear,
                                            currentMonthKey: toMonthKey(
                                              plannerYear,
                                              monthIndex,
                                            ),
                                            baseExpected: stats.baseExpected,
                                            movedHereFrom: stats.movedHereFrom,
                                            row,
                                          })
                                        }
                                        aria-label={`Dettagli ${mesi[monthIndex]} ${plannerYear}`}
                                        title={
                                          stats.expected
                                            ? hasAny
                                              ? "Completata (intervento registrato)"
                                              : "Da completare (nessun intervento)"
                                            : hasAny
                                              ? "Intervento extra"
                                              : ""
                                        }
                                      >
                                        {stats.expected && !hasAny && (
                                          <CircleDashed
                                            size={18}
                                            className="cc-planner-icon cc-planner-icon-due"
                                            style={{ color: "#E53935" }}
                                          />
                                        )}
                                        {stats.expected && hasAny && (
                                          <CheckCircle2
                                            size={18}
                                            className="cc-planner-icon cc-planner-icon-done"
                                            style={{ color: "#28A745" }}
                                          />
                                        )}
                                        {!stats.expected && hasAny && (
                                          <CircleDashed
                                            size={18}
                                            className="cc-planner-icon cc-planner-icon-extra"
                                            style={{ color: "#F57F17" }}
                                          />
                                        )}
                                      </button>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {selectedPlannerCell?.row && (
            <div
              className="cc-modal-overlay"
              onClick={() => setSelectedPlannerCell(null)}
            >
              <div
                className="cc-modal cc-planner-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                {(() => {
                  const { row, monthIndex, year } = selectedPlannerCell;
                  const stats = getMonthStats(row, year, monthIndex);
                  return (
                    <>
                      <div className="cc-modal-header">
                        <div>
                          <h2>
                            {row.clienteNome} • {mesi[monthIndex]} {year}
                          </h2>
                          <div className="cc-planner-modal-subtitle">
                            {(row.sede || "—") +
                              " • " +
                              (row.attivitaDescrizione || "—")}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="cc-icon-btn"
                          onClick={() => setSelectedPlannerCell(null)}
                          aria-label="Chiudi"
                          title="Chiudi"
                        >
                          ×
                        </button>
                      </div>

                      <div className="cc-modal-body">
                        <h3 className="cc-section-title">Interventi</h3>
                        {stats.interventi.length === 0 ? (
                          <div className="cc-empty-block">
                            Nessun intervento.
                          </div>
                        ) : (
                          <ul className="cc-planner-details-list">
                            {stats.interventi.map((i) => (
                              <li key={i.id}>
                                {i.data || "—"} • {i.descrizione || "—"}{" "}
                                {i.eseguitoDa ? `• ${i.eseguitoDa}` : ""}
                              </li>
                            ))}
                          </ul>
                        )}

                        {plannerOffsetBaseKey && (
                          <>
                            <h3 className="cc-section-title">Pianificazione</h3>
                            <div className="cc-grid-2">
                              <div className="cc-field">
                                <label>Prevista</label>
                                <input
                                  type="text"
                                  value={monthKeyToLabel(plannerOffsetBaseKey)}
                                  disabled
                                />
                              </div>
                              <div className="cc-field">
                                <label>Spostata a</label>
                                <input
                                  type="month"
                                  value={plannerOffsetTargetKey}
                                  onChange={(e) => {
                                    setPlannerOffsetTargetKey(e.target.value);
                                    setPlannerOffsetFeedback(null);
                                  }}
                                />
                              </div>
                            </div>
                            <div className="cc-footer-actions cc-planner-offset-actions">
                              <button
                                type="button"
                                className="cc-btn cc-btn-primary"
                                disabled={
                                  plannerOffsetSaving || !plannerOffsetTargetKey
                                }
                                onClick={savePlannerOffset}
                              >
                                Salva spostamento
                              </button>
                              <button
                                type="button"
                                className="cc-btn cc-btn-secondary"
                                disabled={
                                  plannerOffsetSaving ||
                                  !plannerOffsetTargetKey ||
                                  plannerOffsetTargetKey ===
                                    plannerOffsetBaseKey
                                }
                                onClick={deletePlannerOffset}
                              >
                                Ripristina
                              </button>
                            </div>
                            {plannerOffsetFeedback?.message && (
                              <div
                                className={`cc-planner-offset-feedback ${
                                  plannerOffsetFeedback.type === "success"
                                    ? "success"
                                    : "error"
                                }`}
                              >
                                {plannerOffsetFeedback.message}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <div className="cc-modal-footer">
                        <span />
                        <div className="cc-footer-actions">
                          <button
                            type="button"
                            className="cc-btn cc-btn-secondary"
                            onClick={() => {
                              setSelectedPlannerCell(null);
                              setMainTab("clienti");
                              openEdit(row.__cliente, "manutenzione");
                            }}
                          >
                            Apri cliente
                          </button>
                          <button
                            type="button"
                            className="cc-btn cc-btn-secondary"
                            onClick={() => setSelectedPlannerCell(null)}
                          >
                            Chiudi
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className="cc-modal-overlay" onClick={closeModal}>
          <div
            className="cc-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="cc-modal-header">
              <h2>{editingCliente ? "Dettaglio Cliente" : "Nuovo Cliente"}</h2>
              <button
                type="button"
                className="cc-icon-btn"
                onClick={closeModal}
                aria-label="Chiudi"
                title="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="cc-tabs">
              <button
                type="button"
                className={`cc-tab ${activeTab === "generali" ? "active" : ""}`}
                onClick={() => setActiveTab("generali")}
              >
                Dati Cliente
              </button>
              <button
                type="button"
                className={`cc-tab ${activeTab === "tariffe" ? "active" : ""}`}
                onClick={() => setActiveTab("tariffe")}
              >
                Tariffe
              </button>
              <button
                type="button"
                className={`cc-tab ${
                  activeTab === "manutenzione" ? "active" : ""
                }`}
                onClick={() => setActiveTab("manutenzione")}
              >
                Manutenzione
              </button>
              <button
                type="button"
                className={`cc-tab ${
                  activeTab === "reperibilita" ? "active" : ""
                }`}
                onClick={() => setActiveTab("reperibilita")}
              >
                Reperibilità
              </button>
            </div>

            <div className="cc-modal-body">
              {activeTab === "generali" && (
                <div className="cc-form">
                  <div className="cc-field">
                    <label>Nome / Ragione sociale</label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setField("nome", e.target.value)}
                    />
                  </div>

                  <div className="cc-grid-2">
                    <div className="cc-field">
                      <label>Partita IVA</label>
                      <input
                        type="text"
                        value={formData.partitaIva}
                        onChange={(e) => setField("partitaIva", e.target.value)}
                      />
                    </div>
                    <div className="cc-field">
                      <label>Codice Fiscale</label>
                      <input
                        type="text"
                        value={formData.codiceFiscale}
                        onChange={(e) =>
                          setField("codiceFiscale", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="cc-field">
                    <label>Indirizzo</label>
                    <input
                      type="text"
                      value={formData.indirizzo}
                      onChange={(e) => setField("indirizzo", e.target.value)}
                    />
                  </div>

                  <div className="cc-grid-2">
                    <div className="cc-field">
                      <label>Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setField("email", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="cc-field">
                    <label>Project di riferimento</label>
                    <Select
                      classNamePrefix="react-select"
                      options={projectOptions}
                      value={
                        projectOptions.find(
                          (o) =>
                            o.value ===
                            (formData.projectCodiceRiferimento ?? ""),
                        ) || null
                      }
                      onChange={(opt) =>
                        setField(
                          "projectCodiceRiferimento",
                          opt ? opt.value : "",
                        )
                      }
                      isClearable={true}
                      isSearchable={true}
                      placeholder="Seleziona project..."
                      noOptionsMessage={() => "Nessun project trovato"}
                    />
                  </div>
                </div>
              )}

              {activeTab === "tariffe" && (
                <div className="cc-form">
                  <div className="cc-manutenzione-top">
                    <button
                      type="button"
                      className="cc-btn cc-btn-secondary"
                      onClick={addTariffaIntervento}
                    >
                      Aggiungi tariffa
                    </button>
                  </div>

                  {(formData.tariffeIntervento || []).length === 0 && (
                    <div className="cc-empty-block">Nessuna tariffa.</div>
                  )}

                  {(formData.tariffeIntervento || []).map((t, idx) => (
                    <div key={t.id} className="cc-manutenzione-card">
                      <div className="cc-manutenzione-card-header">
                        <div className="cc-manutenzione-title">
                          Tariffa #{idx + 1}
                          {t.descrizione && (
                            <span className="cc-manutenzione-subtitle">
                              {" "}
                              • {t.descrizione}
                            </span>
                          )}
                        </div>
                        <div className="cc-manutenzione-actions">
                          <button
                            type="button"
                            className="cc-btn cc-btn-danger"
                            onClick={() => deleteTariffaIntervento(t.id)}
                          >
                            Rimuovi
                          </button>
                        </div>
                      </div>

                      <div className="cc-field">
                        <label>Descrizione</label>
                        <input
                          type="text"
                          value={t.descrizione || ""}
                          onChange={(e) =>
                            updateTariffaIntervento(t.id, {
                              descrizione: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="cc-grid-2">
                        <div className="cc-field">
                          <label>Manodopera Oraria (€)</label>
                          <input
                            type="number"
                            value={t.manodoperaOraria ?? ""}
                            onChange={(e) =>
                              updateTariffaIntervento(t.id, {
                                manodoperaOraria: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="cc-field">
                          <label>Diritto di Chiamata (€)</label>
                          <input
                            type="number"
                            value={t.dirittoChiamata ?? ""}
                            onChange={(e) =>
                              updateTariffaIntervento(t.id, {
                                dirittoChiamata: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="cc-field">
                    <label>Note</label>
                    <textarea
                      rows="4"
                      value={formData.tariffeNote || ""}
                      onChange={(e) => setField("tariffeNote", e.target.value)}
                    />
                  </div>
                </div>
              )}

              {activeTab === "manutenzione" && (
                <div className="cc-form">
                  <div className="cc-manutenzione-top">
                    <button
                      type="button"
                      className="cc-btn cc-btn-secondary"
                      onClick={addManutenzione}
                    >
                      Aggiungi canone manutenzione
                    </button>
                  </div>

                  {(formData.manutenzioni || []).length === 0 && (
                    <div className="cc-empty-block">
                      Nessun canone manutenzione.
                    </div>
                  )}

                  {(formData.manutenzioni || []).map((m, idx) => (
                    <div key={m.id} className="cc-manutenzione-card">
                      <div className="cc-manutenzione-card-header">
                        <div className="cc-manutenzione-title">
                          Canone #{idx + 1}
                          {(m.sede || m.descrizione) && (
                            <span className="cc-manutenzione-subtitle">
                              {m.sede ? ` • ${m.sede}` : ""}
                              {m.descrizione ? ` • ${m.descrizione}` : ""}
                            </span>
                          )}
                        </div>
                        <div className="cc-manutenzione-actions">
                          <label className="cc-checkbox">
                            <input
                              type="checkbox"
                              checked={Boolean(m.attivo)}
                              onChange={(e) =>
                                updateManutenzione(m.id, {
                                  attivo: e.target.checked,
                                })
                              }
                            />
                            Attivo
                          </label>
                          <button
                            type="button"
                            className="cc-btn cc-btn-danger"
                            onClick={() => deleteManutenzione(m.id)}
                          >
                            Rimuovi
                          </button>
                        </div>
                      </div>

                      <div className="cc-grid-2">
                        <div className="cc-field">
                          <label>Sede</label>
                          <input
                            type="text"
                            value={m.sede || ""}
                            onChange={(e) =>
                              updateManutenzione(m.id, { sede: e.target.value })
                            }
                          />
                        </div>
                        <div className="cc-field">
                          <label>Descrizione</label>
                          <input
                            type="text"
                            value={m.descrizione || ""}
                            onChange={(e) =>
                              updateManutenzione(m.id, {
                                descrizione: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="cc-grid-2">
                        <div className="cc-field">
                          <label>Canone (€)</label>
                          <input
                            type="number"
                            value={m.importo ?? ""}
                            onChange={(e) =>
                              updateManutenzione(m.id, {
                                importo: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="cc-field">
                          <label>Frequenza</label>
                          <select
                            value={m.frequenza || ""}
                            onChange={(e) =>
                              updateManutenzione(m.id, {
                                frequenza: e.target.value,
                              })
                            }
                          >
                            <option value="">Seleziona...</option>
                            <option value="Mensile">Mensile</option>
                            <option value="Trimestrale">Trimestrale</option>
                            <option value="Quadrimestrale">
                              Quadrimestrale
                            </option>
                            <option value="Semestrale">Semestrale</option>
                            <option value="Annuale">Annuale</option>
                          </select>
                        </div>
                      </div>

                      <div className="cc-field">
                        <label>Scadenza</label>
                        <input
                          type="date"
                          value={m.scadenza || ""}
                          onChange={(e) =>
                            updateManutenzione(m.id, {
                              scadenza: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="cc-field">
                        <label>Settore</label>
                        <Select
                          classNamePrefix="react-select"
                          options={settoreOptions}
                          value={
                            settoreOptions.find(
                              (o) =>
                                o.value === (m.settoreId || "") ||
                                o.label === (m.settoreNome || ""),
                            ) || null
                          }
                          onChange={(opt) =>
                            updateManutenzione(m.id, {
                              settoreId: opt?.value || "",
                              settoreNome: opt?.label || "",
                            })
                          }
                          isClearable={true}
                          isSearchable={true}
                          placeholder="Seleziona settore..."
                          noOptionsMessage={() => "Nessun settore trovato"}
                        />
                      </div>

                      <h3 className="cc-section-title">Attività</h3>
                      <div className="cc-manutenzione-top">
                        <button
                          type="button"
                          className="cc-btn cc-btn-secondary"
                          onClick={() => addAttivita(m.id)}
                        >
                          Aggiungi attività
                        </button>
                      </div>

                      {(m.attivita || []).length === 0 && (
                        <div className="cc-empty-block">
                          Nessuna attività configurata.
                        </div>
                      )}

                      {(m.attivita || []).map((a, aIdx) => (
                        <div key={a.id} className="cc-attivita-card">
                          <div className="cc-manutenzione-card-header">
                            <div className="cc-manutenzione-title">
                              Attività #{aIdx + 1}
                              {a.descrizione && (
                                <span className="cc-manutenzione-subtitle">
                                  {" "}
                                  • {a.descrizione}
                                </span>
                              )}
                            </div>
                            <div className="cc-manutenzione-actions">
                              <button
                                type="button"
                                className="cc-btn cc-btn-danger"
                                onClick={() => deleteAttivita(m.id, a.id)}
                              >
                                Rimuovi
                              </button>
                            </div>
                          </div>

                          <div className="cc-grid-2">
                            <div className="cc-field">
                              <label>Descrizione attività</label>
                              <input
                                type="text"
                                value={a.descrizione || ""}
                                onChange={(e) =>
                                  updateAttivita(m.id, a.id, {
                                    descrizione: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div className="cc-field">
                              <label>Frequenza</label>
                              <select
                                value={a.frequenza || ""}
                                onChange={(e) =>
                                  updateAttivita(m.id, a.id, {
                                    frequenza: e.target.value,
                                  })
                                }
                              >
                                <option value="">Seleziona...</option>
                                <option value="Mensile">Mensile</option>
                                <option value="Trimestrale">Trimestrale</option>
                                <option value="Quadrimestrale">
                                  Quadrimestrale
                                </option>
                                <option value="Semestrale">Semestrale</option>
                                <option value="Annuale">Annuale</option>
                              </select>
                            </div>
                          </div>

                          <div className="cc-field">
                            <label>Mese di inizio</label>
                            <select
                              value={
                                typeof a.meseInizio === "number"
                                  ? a.meseInizio
                                  : ""
                              }
                              onChange={(e) =>
                                updateAttivita(m.id, a.id, {
                                  meseInizio:
                                    e.target.value === ""
                                      ? ""
                                      : Number(e.target.value),
                                })
                              }
                            >
                              <option value="">Seleziona...</option>
                              {mesi.map((label, idx2) => (
                                <option key={label} value={idx2}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <h3 className="cc-section-title">
                            Registro interventi
                          </h3>
                          <div className="cc-subtable-wrap">
                            <table className="cc-subtable">
                              <thead>
                                <tr>
                                  <th>Data</th>
                                  <th>Descrizione</th>
                                  <th>Eseguito da</th>
                                  <th>Azioni</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(a.registroInterventi || []).map((r) => (
                                  <tr key={r.id}>
                                    <td>
                                      <input
                                        type="date"
                                        value={r.data || ""}
                                        onChange={(e) =>
                                          updateAttivitaRow(
                                            m.id,
                                            a.id,
                                            "registroInterventi",
                                            r.id,
                                            "data",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        value={r.descrizione || ""}
                                        onChange={(e) =>
                                          updateAttivitaRow(
                                            m.id,
                                            a.id,
                                            "registroInterventi",
                                            r.id,
                                            "descrizione",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        value={r.eseguitoDa || ""}
                                        onChange={(e) =>
                                          updateAttivitaRow(
                                            m.id,
                                            a.id,
                                            "registroInterventi",
                                            r.id,
                                            "eseguitoDa",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="cc-btn cc-btn-danger"
                                        onClick={() =>
                                          deleteAttivitaRow(
                                            m.id,
                                            a.id,
                                            "registroInterventi",
                                            r.id,
                                          )
                                        }
                                      >
                                        Elimina
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                                {(a.registroInterventi || []).length === 0 && (
                                  <tr>
                                    <td
                                      colSpan="4"
                                      style={{ textAlign: "center" }}
                                    >
                                      Nessun intervento.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <button
                            type="button"
                            className="cc-btn cc-btn-secondary"
                            onClick={() =>
                              addAttivitaRow(m.id, a.id, "registroInterventi", {
                                data: "",
                                descrizione: "",
                                eseguitoDa: "",
                              })
                            }
                          >
                            Aggiungi intervento
                          </button>
                        </div>
                      ))}

                      <h3 className="cc-section-title">
                        Registro fatturazioni canone
                      </h3>
                      <div className="cc-subtable-wrap">
                        <table className="cc-subtable">
                          <thead>
                            <tr>
                              <th>Data</th>
                              <th>Riferimento</th>
                              <th>Importo (€)</th>
                              <th>Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(m.registroFatturazioni || []).map((r) => (
                              <tr key={r.id}>
                                <td>
                                  <input
                                    type="date"
                                    value={r.data || ""}
                                    onChange={(e) =>
                                      updateManutenzioneRow(
                                        m.id,
                                        "registroFatturazioni",
                                        r.id,
                                        "data",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={r.riferimento || ""}
                                    onChange={(e) =>
                                      updateManutenzioneRow(
                                        m.id,
                                        "registroFatturazioni",
                                        r.id,
                                        "riferimento",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={r.importo || ""}
                                    onChange={(e) =>
                                      updateManutenzioneRow(
                                        m.id,
                                        "registroFatturazioni",
                                        r.id,
                                        "importo",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="cc-btn cc-btn-danger"
                                    onClick={() =>
                                      deleteManutenzioneRow(
                                        m.id,
                                        "registroFatturazioni",
                                        r.id,
                                      )
                                    }
                                  >
                                    Elimina
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {(m.registroFatturazioni || []).length === 0 && (
                              <tr>
                                <td colSpan="4" style={{ textAlign: "center" }}>
                                  Nessuna fattura.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <button
                        type="button"
                        className="cc-btn cc-btn-secondary"
                        onClick={() =>
                          addManutenzioneRow(m.id, "registroFatturazioni", {
                            data: "",
                            riferimento: "",
                            importo: "",
                          })
                        }
                      >
                        Aggiungi fattura canone
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "reperibilita" && (
                <div className="cc-form">
                  <div className="cc-field">
                    <label className="cc-checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.reperibilita.attivo)}
                        onChange={(e) =>
                          setNestedField(
                            "reperibilita",
                            "attivo",
                            e.target.checked,
                          )
                        }
                      />
                      Contratto reperibilità attivo
                    </label>
                  </div>

                  {formData.reperibilita.attivo && (
                    <>
                      <div className="cc-grid-2">
                        <div className="cc-field">
                          <label>Canone (€)</label>
                          <input
                            type="number"
                            value={formData.reperibilita.importo}
                            onChange={(e) =>
                              setNestedField(
                                "reperibilita",
                                "importo",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="cc-field">
                          <label>Frequenza</label>
                          <select
                            value={formData.reperibilita.frequenza}
                            onChange={(e) =>
                              setNestedField(
                                "reperibilita",
                                "frequenza",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Seleziona...</option>
                            <option value="Mensile">Mensile</option>
                            <option value="Trimestrale">Trimestrale</option>
                            <option value="Quadrimestrale">
                              Quadrimestrale
                            </option>
                            <option value="Semestrale">Semestrale</option>
                            <option value="Annuale">Annuale</option>
                          </select>
                        </div>
                      </div>

                      <h3 className="cc-section-title">
                        Registro fatturazioni canone
                      </h3>
                      <div className="cc-subtable-wrap">
                        <table className="cc-subtable">
                          <thead>
                            <tr>
                              <th>Data</th>
                              <th>Riferimento</th>
                              <th>Importo (€)</th>
                              <th>Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.reperibilita.registroFatturazioni.map(
                              (r) => (
                                <tr key={r.id}>
                                  <td>
                                    <input
                                      type="date"
                                      value={r.data || ""}
                                      onChange={(e) =>
                                        updateRow(
                                          "reperibilita",
                                          "registroFatturazioni",
                                          r.id,
                                          "data",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={r.riferimento || ""}
                                      onChange={(e) =>
                                        updateRow(
                                          "reperibilita",
                                          "registroFatturazioni",
                                          r.id,
                                          "riferimento",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      value={r.importo || ""}
                                      onChange={(e) =>
                                        updateRow(
                                          "reperibilita",
                                          "registroFatturazioni",
                                          r.id,
                                          "importo",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="cc-btn cc-btn-danger"
                                      onClick={() =>
                                        deleteRow(
                                          "reperibilita",
                                          "registroFatturazioni",
                                          r.id,
                                        )
                                      }
                                    >
                                      Elimina
                                    </button>
                                  </td>
                                </tr>
                              ),
                            )}
                            {formData.reperibilita.registroFatturazioni
                              .length === 0 && (
                              <tr>
                                <td colSpan="4" style={{ textAlign: "center" }}>
                                  Nessuna fattura.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <button
                        type="button"
                        className="cc-btn cc-btn-secondary"
                        onClick={() =>
                          addRow("reperibilita", "registroFatturazioni", {
                            data: "",
                            riferimento: "",
                            importo: "",
                          })
                        }
                      >
                        Aggiungi fattura
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="cc-modal-footer">
              {editingCliente ? (
                <button
                  type="button"
                  className="cc-btn cc-btn-danger"
                  onClick={handleDelete}
                >
                  Elimina cliente
                </button>
              ) : (
                <span />
              )}

              <div className="cc-footer-actions">
                <button
                  type="button"
                  className="cc-btn cc-btn-secondary"
                  onClick={closeModal}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="cc-btn cc-btn-primary"
                  onClick={handleSave}
                >
                  Salva
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
